import './popupUpsert.scss';

import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { confirm } from "devextreme/ui/dialog";
import { Form, Button, Popup, CheckBox, ScrollView, DataGrid, Toast } from "devextreme-react";
import { DataGridRef, Column } from "devextreme-react/data-grid";
import { FormRef, Item } from "devextreme-react/form";
import { Position, ToolbarItem } from "devextreme-react/popup";
import { ClickEvent } from "devextreme/ui/button";
import { useScreenSize } from "../../../../utils/media-query";

import { ATS_RapportoLavoroPersonaleEdit } from "..";
import { PopupPersonaleEditHandle } from "./popupPersonaleEdit";

import useApiConfiguration from "../../../../apiConfig";
import {
    PersonaleRapportinoDTO, PersonaleRapportinoDTOFromJSON, PersonaleRapportinoDTOToJSON,
    RapportiniApi, RapportiniCreateDTO
} from "../../../../data";
import { useClienteDataSource } from "../../../../datasources/clienteDataSource";
import { useCommessaDataSource } from "../../../../datasources/commessaDataSource";

export type PopupCreateHandle = {
    show: (tipologia: string) => void
};

type Props = {
    onChange: () => void
};

type State = {
    tipologia: string,
    visible: boolean
}

const PopupCreate: React.ForwardRefRenderFunction<PopupCreateHandle, Props> = (props, ref) => {
    const emptyData: RapportiniCreateDTO = {
        idCommessa: undefined,
        idCliente: undefined,
        tipoRapportino: null,
        dataRapportino: undefined,
        descrizioneAttivita: null,
        ubicazioneTest: null,
        descrizioneLavori: null,
        strumentiMisura: null,
        isRelazioneProva: false,
        isSchedaTecnica: false,
        luogo: null,
        personaRiferimento: null,
        isTrimestrale: false,
        isSemestrale: false,
        isAnnuale: false,
        isManutenzioneStraordinaria: false,
        dataRiferimentoOrdine: null,
        riferimentoDDT: null,
        dataRiferimentoDDT: null,
        attrezzatureSpeciali: null,
        isCheckList: false,
        isReportAnomalie: false,
        isReportIntervento: false,
        personaleRapportino: []
    };

    const [state, setState] = useState<State>({ tipologia: '', visible: false });
    const [toastConfig, setToastConfig] = useState({ isVisible: false, message: '' } as { isVisible: boolean, message: string });
    const [formData, setFormData] = useState<RapportiniCreateDTO>();
    const [clienteId, setClienteId] = useState<number | null>(null);
    const [canChangeTestata, setCanChangeTestata] = useState(true);
    const [canChangePersonale, setCanChangePersonale] = useState(false);

    const formRef = useRef<FormRef>(null);
    const gridRef = useRef<DataGridRef>(null);
    const popupPersonaleEditRef = useRef<PopupPersonaleEditHandle>(null);
    const apiConfig = useApiConfiguration();
    const { isLarge, isMedium, isSmall, isXSmall } = useScreenSize();

    const clienteDataSource = useClienteDataSource();
    const commessaDataSource = useCommessaDataSource(null, null, clienteId);
    const [personaleLst, setPersonaleLst] = useState<PersonaleRapportinoDTO[]>([]);

    function hideInfo() {
        setState({ tipologia: '', visible: false });
    }

    useImperativeHandle(ref, () => ({
        show(tipologia) {
            setState({ tipologia: tipologia, visible: true });
        }
    }));

    useEffect(() => {
        if (state.visible) {
            setPersonaleLst([]);

            setFormData({
                ...emptyData,
                tipoRapportino: state.tipologia
            });
        }
    }, [state.tipologia, state.visible]);

    const onHidingToast = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false
        });
    }, [toastConfig, setToastConfig]);

    const onFieldDataChanged = (e) => {
        const { dataField, value } = e;

        if (dataField === "idCliente" && value !== clienteId) {
            formData.idCommessa = null;
            setCanChangePersonale(false);
            setClienteId(value || null);
        }

        if (dataField === "idCommessa" || dataField === "dataRapportino") {
            setCanChangePersonale((formData.idCommessa != null) && (formData.dataRapportino != undefined && formData.dataRapportino != null))
        }
    };

    const insert = (e: ClickEvent) => {
        const validate = formRef.current?.instance().validate();

        if (validate?.isValid) {
            if ((personaleLst ?? []).length == 0) {
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    message: 'Inserire il personale'
                });
            } else {
                formData.personaleRapportino = personaleLst;

                var api = new RapportiniApi(apiConfig);
                api.insertRapportinoRaw({
                    rapportiniCreateDTO: formData
                })
                    .then(() => props.onChange())
                    .finally(() => hideInfo());
            }
        }
    };

    const openPersonale = (rowIdx: number) => {
        if (rowIdx == -1) {
            if (personaleLst.length > 0) {
                var obj = personaleLst.find((_, idx) => idx === 0);
                var json = JSON.stringify(PersonaleRapportinoDTOToJSON(obj));
                popupPersonaleEditRef.current.show(json, formData.idCommessa, formData.dataRapportino, false);
            } else
                popupPersonaleEditRef.current.show('', formData.idCommessa, formData.dataRapportino, false);
        } else {
            var obj = personaleLst.find((_, idx) => idx === rowIdx);
            var json = JSON.stringify(PersonaleRapportinoDTOToJSON(obj));
            popupPersonaleEditRef.current.show(json, formData.idCommessa, formData.dataRapportino, true);
        }
    };

    const upsertPersonale = (json: string) => {
        const objNew = PersonaleRapportinoDTOFromJSON(JSON.parse(json));
        
        setPersonaleLst(prev => {
            const list = prev ?? [];
            const idx = list.findIndex(it => it.idPersonale === objNew.idPersonale);

            if (idx >= 0) {
                const next = list.slice();
                next[idx] = objNew;
                return next;
            }

            return [ ...list, objNew ];
        });
    };

    function checkDeletePersonale(rowIndex: number) {
        const result = confirm("Confermi l'eliminazione?", "Conferma");
        result.then((dialogRes) => {
            if (dialogRes)
                deletePersonale(rowIndex);
        });
    };

    function deletePersonale(rowIndex: number) {
        setPersonaleLst(prev => {
            const list = prev ?? [];
            return list.filter((_, idx) => idx !== rowIndex);
        });

        gridRef.current?.instance().refresh();
    };

    useEffect(() => {
        setCanChangeTestata((personaleLst ?? []).length == 0);
    }, [personaleLst]);

    return (
        <React.Fragment>
            <Popup
                enableBodyScroll={false}
                visible={state.visible}
                dragEnabled={false}
                hideOnOutsideClick={true}
                showCloseButton={true}
                fullScreen={(isSmall || isXSmall ? true : false)}
                showTitle={true}
                title={"Nuovo Rapporto M. 25"}
                container=".dx-viewport"
                height={"85%"}
                onHidden={hideInfo}
            >
                <Position at="center" my="center" of="" collision="fit" />

                <ToolbarItem widget="dxButton" toolbar="bottom" location="center">
                    <Button
                        onClick={insert}
                        icon="check"
                        text="Salva"
                        type="default"
                        stylingMode="contained"
                    />
                </ToolbarItem>

                <ScrollView className="rapportino-create-scroll">
                    <Form
                        ref={formRef} formData={formData}
                        labelMode="floating" labelLocation="left"
                        colCount={(isLarge || isMedium) ? 4 : 2}
                        style={{ padding: "10px" }}
                        onFieldDataChanged={onFieldDataChanged}
                    >
                        <Item visible={false} dataField="tipoRapportino" />

                        <Item colSpan={(isLarge || isMedium) ? 1 : 2} dataField="dataRapportino" isRequired={true}
                            label={{ text: "Data" }} editorType="dxDateBox"
                            editorOptions={{
                                type: "date",
                                displayFormat: (isLarge || isMedium) ? "EEEE d MMMM yyyy" : "dd/MM/yyyy",
                                openOnFieldClick: true,
                                disabled: !canChangeTestata
                            }}
                        />

                        <Item colSpan={3} visible={(isLarge || isMedium)} itemType="empty" />

                        <Item colSpan={2} dataField="idCliente" label={{ text: "Cliente" }}
                            editorType="dxSelectBox" isRequired={true}
                            editorOptions={{
                                dataSource: clienteDataSource,
                                displayExpr: 'ragioneSociale',
                                valueExpr: 'idCliente',
                                searchEnabled: true,
                                showClearButton: true,
                                disabled: !canChangeTestata
                            }}
                        />

                        <Item colSpan={2} dataField="idCommessa" label={{ text: "Commessa" }}
                            editorType="dxLookup" isRequired={true}
                            editorOptions={{
                                dataSource: commessaDataSource,
                                disabled: !clienteId || !canChangeTestata,
                                displayExpr: 'descrizione',
                                valueExpr: 'idCommessa',
                                searchEnabled: true
                            }}
                        />

                        <Item colSpan={2} dataField="descrizioneAttivita" label={{ text: "Descrizione Attività" }} editorType="dxTextBox" isRequired={true} />

                        <Item colSpan={2} dataField="ubicazioneTest" label={{ text: "Ubicazione Test / Attività" }} editorType="dxTextBox" isRequired={true} />

                        <Item colSpan={(isLarge || isMedium) ? 4 : 2} dataField="descrizioneLavori"
                            editorType="dxTextArea" isRequired={true} cssClass="dx-textarea-custom"
                            editorOptions={{
                                maxLength: 1500,
                                autoResizeEnabled: true
                            }}
                        />

                        <Item colSpan={2} dataField="strumentiMisura" label={{ text: "Strumenti di Misura utilizzati" }}
                            editorType="dxTextArea" cssClass="dx-textarea-custom"
                            editorOptions={{
                                maxLength: 250,
                                autoResizeEnabled: true
                            }}
                        />

                        <Item colSpan={2}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                                <CheckBox
                                    defaultValue={formData?.isRelazioneProva}
                                    onValueChanged={(e) => formData.isRelazioneProva = e.value}
                                    text="Relazione di prova"
                                />
                                <CheckBox
                                    defaultValue={formData?.isSchedaTecnica}
                                    onValueChanged={(e) => formData.isSchedaTecnica = e.value}
                                    text="Scheda tecnica"
                                />
                            </div>
                        </Item>

                        <Item colSpan={(isLarge || isMedium) ? 4 : 2}>
                            <div style={{ display: "flex", justifyContent: "end", marginTop: "10px", marginBottom: "10px" }}>
                                <Button disabled={!canChangePersonale} icon="add" text="Aggiungi Personale" stylingMode="text" onClick={() => openPersonale(-1)} />
                            </div>
                            <DataGrid
                                className="grid theme-dependent" ref={gridRef} dataSource={personaleLst}
                                rowAlternationEnabled={true} defaultFocusedRowIndex={0} noDataText="Nessun personale caricato"
                            >
                                <Column
                                    width={100}
                                    cellRender={(cellData) => (
                                        <div style={{ display: "flex" }}>
                                            <Button disabled={!canChangePersonale} icon="edit" hint="Modifica" stylingMode="text" onClick={() => openPersonale(cellData.rowIndex)} />
                                            <Button disabled={!canChangePersonale} icon="trash" hint="Elimina" stylingMode="text" onClick={() => checkDeletePersonale(cellData.rowIndex)} />
                                        </div>
                                    )}
                                />
                                <Column dataField="personale" />
                                <Column dataField="oreTotali" />
                                <Column dataField="oreViaggio" />
                                <Column dataField="viaggio" caption="Viaggi (KM)" />
                                <Column dataField="pernottamenti" />
                            </DataGrid>
                        </Item>
                    </Form>
                </ScrollView>
            </Popup>

            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                height={45} width={200}
                position={{ at: 'right bottom', my: 'right bottom', of: window, offset: { x: -20, y: -20 } }}
                type='error' displayTime={3000}
                onHiding={onHidingToast}
                animation={{ show: { type: 'fade', duration: 400, from: 0, to: 1 }, hide: { type: 'fade', duration: 40, to: 0 } }}
            />

            <ATS_RapportoLavoroPersonaleEdit ref={popupPersonaleEditRef} onChange={upsertPersonale} />
        </React.Fragment>
    )
}

export default React.forwardRef(PopupCreate);